import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";

export function Section({ id, eyebrow, title, subtitle, children, className = "" }) {
  return (
    <section id={id} className={`py-16 md:py-24 ${className}`}>
      <div className="app-container">
        {(eyebrow || title || subtitle) && (
          <div className="max-w-3xl mb-10 md:mb-14">
            {eyebrow && <div className="eyebrow mb-4">{eyebrow}</div>}
            {title && <h2 className="font-display text-3xl md:text-5xl leading-[1.05] mb-4 max-w-[22ch]">{title}</h2>}
            {subtitle && <p className="text-white/72 md:text-lg leading-relaxed section-subtitle">{subtitle}</p>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

export function CategoryCard({ to, image, title, tag, testid }) {
  return (
    <Link to={to} className="group relative overflow-hidden card-surface focus-ring transition-transform hover:-translate-y-0.5" data-testid={testid}>
      <div className="aspect-[4/5] w-full overflow-hidden">
        <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" loading="lazy" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
      <div className="absolute bottom-0 inset-x-0 p-5">
        {tag && <div className="text-[10px] tracking-[0.28em] uppercase gold-text mb-1.5">{tag}</div>}
        <div className="font-display text-lg md:text-xl leading-snug max-w-[18ch]">{title}</div>
      </div>
    </Link>
  );
}

export function ArticleCard({ article, testid }) {
  return (
    <Link to={`/article/${article.slug}`} className="card-surface overflow-hidden group focus-ring flex flex-col" data-testid={testid || `article-${article.slug}`}>
      <div className="aspect-[16/10] overflow-hidden">
        <img src={article.image} alt={article.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="text-[10px] tracking-[0.28em] uppercase gold-text mb-2">{article.category?.replace(/-/g, " ")}</div>
        <div className="font-display text-lg leading-snug max-w-[28ch]">{article.title}</div>
        <div className="text-sm text-white/65 mt-2 line-clamp-2 leading-relaxed">{article.summary}</div>
        <div className="mt-4 text-xs text-white/50">{article.read_time} min read</div>
      </div>
    </Link>
  );
}

export function ProgramCard({ program }) {
  return (
    <div className="card-surface overflow-hidden" data-testid={`program-${program.slug}`}>
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={program.image} alt={program.title} className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute top-3 left-3 chip chip-active">{program.badge}</div>
      </div>
      <div className="p-4">
        <div className="font-display text-xl">{program.title}</div>
        <div className="text-sm text-white/65 mt-1">{program.duration_weeks} weeks · {program.level}</div>
        <ul className="mt-3 space-y-1 text-sm text-white/75">
          {program.outcomes.slice(0, 3).map((o, i) => <li key={i} className="flex gap-2"><span className="gold-text">◆</span>{o}</li>)}
        </ul>
        <Link to="/shop" className="btn-secondary mt-4 inline-flex items-center gap-2 text-sm">
          Start <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

export function Pillar({ label, icon: Icon }) {
  return (
    <div className="card-surface p-4 flex items-center gap-3">
      {Icon ? <Icon className="w-5 h-5 gold-text" /> : <span className="gold-text">◆</span>}
      <div className="text-sm">{label}</div>
    </div>
  );
}

export function SkeletonCard({ aspect = "4/5" }) {
  return (
    <div className="card-surface overflow-hidden">
      <div className="shimmer" style={{ aspectRatio: aspect }} />
      <div className="p-4">
        <div className="h-3 shimmer rounded w-1/3 mb-2" />
        <div className="h-4 shimmer rounded w-3/4" />
      </div>
    </div>
  );
}
